import React, { useState } from "react";
import useAuthUser from "../../base/helper/useAuthUser";
import usePICostReports from "../helper/usePICostReports";
import MaterialTable from "material-table";
import { format, parseISO } from "date-fns";
import { RemoveRedEye } from "@material-ui/icons";
import { info, success, warning } from "../../../../store/notification/actions";
import axiosInstance from "../../../../axiosInstance";
import CustomModal from "../../base/views/CustomModal";
import { useMemo } from "react";
import { exportPICostCsv, exportPICostPdf } from "../helper/piCostReportHelper";
import { Box, Typography } from "@material-ui/core";

function PhysicalInventoryCostReports({ fromDate, toDate }) {
  const { storeId, store } = useAuthUser();
  const { isLoading, reports } = usePICostReports(storeId, fromDate, toDate);
  const [reportId, setReportId] = useState(null);
  const [reportData, setReportData] = useState([]);

  const getReport = async (row) => {
    setReportId(row.reportid);
    try {
      info("Fetching report...");
      const result = await axiosInstance.post(
        "/physicalInventory/getPICostReport",
        {
          storeId,
          reportId: row.reportid,
        }
      );
      setReportData(result.data.data);
      success("Report fetched");
    } catch (error) {
      warning("There was an error");
    }
  };

  const reportTitle = useMemo(() => {
    let title = `Report_${reportId}`;
    if (reportData.length) {
      if (reportData[0].startdate && reportData[0].enddate) {
        title += `_(${format(
          parseISO(reportData[0].startdate),
          "MM-dd-yyyy"
        )}_${format(parseISO(reportData[0].enddate), "MM-dd-yyyy")})`;
      }
    }
    return title;
  }, [reportId, reportData]);

  function exportCsvHandler(columns, data) {
    exportPICostCsv(columns, data, reportTitle);
  }

  function exportPdfHandler(columns, data) {
    exportPICostPdf(columns, data, reportTitle, store);
  }

  return (
    <>
      <MaterialTable
        title={"Inventory Cost Reports"}
        data={reports}
        isLoading={isLoading}
        columns={[
          {
            title: "Report ID",
            field: "reportid",
          },
          {
            title: "From Date",
            render: (row) => {
              return (
                row.startdate && format(parseISO(row.startdate), "MM-dd-yyyy")
              );
            },
          },
          {
            title: "To Date",
            render: (row) => {
              return row.enddate && format(parseISO(row.enddate), "MM-dd-yyyy");
            },
          },
          {
            title: "Report Date",
            field: "reportdate",
            render: (row) => {
              return format(parseISO(row.reportdate), "MM-dd-yyyy");
            },
          },
          {
            title: "Total",
            field: "total",
            render: (row) =>
              row.total &&
              Number(row.total).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              }),
          },
        ]}
        options={{
          paging: true,
          pageSize: 50,
          pageSizeOptions: [25, 50, 100, 250, 500],
          actionsColumnIndex: 5,
        }}
        actions={[
          {
            icon: () => <RemoveRedEye />,
            tooltip: "View Report",
            onClick: (event, data) => getReport(data),
            position: "row",
          },
        ]}
      />
      <CustomModal
        open={reportId !== null}
        onClose={() => {
          setReportData([]);
          setReportId(null);
        }}
      >
        {" "}
        <Box
          style={{
            width: "100%",
            padding: 10,
          }}
        >
          <Typography variant="h6">{reportTitle}</Typography>
        </Box>
        <MaterialTable
          style={{
            height: "90vh",
            width: "100%",
            overflow: "scroll",
            overflowX: "scroll",
          }}
          title={` Report total:
            ${Number(reportData[0]?.report_total || 0).toLocaleString("en-us", {
              style: "currency",
              currency: "USD",
            })}`}
          columns={[
            { title: "NDC", field: "ndc" },
            { title: "Description", field: "description" },
            { title: "Pack Size", field: "pack_size", type: "numeric" },
            { title: "Drug CD", field: "drug_cd" },
            { title: "Form", field: "form" },
            { title: "Gen Status", field: "gen_status" },
            {
              title: "Inventory Count (Units)",
              field: "inventory_count",
              type: "numeric",
            },
            {
              title: "Unit Cost",
              field: "unit_cost",
              type: "numeric",
              render: (row) =>
                row.unit_cost &&
                Number(row.unit_cost).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }),
            },
            {
              title: "Ext Cost",
              field: "ext_cost",
              type: "numeric",
              render: (row) =>
                row.ext_cost &&
                Number(row.ext_cost).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }),
            },
          ]}
          data={reportData}
          options={{
            paging: true,
            pageSize: 25,
            pageSizeOptions: [25, 50, 100, 250, 500],
            actionsColumnIndex: 5,
            exportButton: true,
            exportCsv: exportCsvHandler,
            exportPdf: exportPdfHandler,
            exportAllData: true,
          }}
        />
      </CustomModal>
    </>
  );
}

export default PhysicalInventoryCostReports;
